import { RefreshCw } from 'lucide-react';

import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/button';
import { useRefreshRenewalAum } from '@/hooks/useRenewals';
import type { RenewalDetail } from '@/services/renewals.service';
import { errorMessage } from '@/utils/apiError';
import cn from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

interface LiveAumStatusProps {
  detail: RenewalDetail;
}

// Where the AUM on screen came from, plus an on-demand re-fetch. The fetch also
// re-prices the renewal, so the amount shown is the one the next reminder quotes.
export const LiveAumStatus = ({ detail }: LiveAumStatusProps) => {
  const { toast } = useToast();
  const refresh = useRefreshRenewalAum(detail.id);
  const fetchedAt = detail.client.aumFetchedAt;

  const onRefresh = () => {
    refresh.mutate(undefined, {
      onSuccess: ({ outcome }) => {
        if (outcome === 'refreshed') {
          toast.success('Latest AUM fetched from AMFI');
        } else {
          toast.warning('Live AUM is unavailable right now - the stored AUM is still in use');
        }
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not fetch the live AUM')),
    });
  };

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <p className="text-[11px] text-muted-foreground">
        {fetchedAt
          ? `Live AUM from AMFI · fetched ${formatDateTime(fetchedAt)}`
          : 'Live AUM not fetched yet - showing the stored AUM'}
      </p>
      <Button size="sm" variant="outline" onClick={onRefresh} disabled={refresh.isPending}>
        <RefreshCw className={cn('mr-1 h-3.5 w-3.5', refresh.isPending && 'animate-spin')} />
        {refresh.isPending ? 'Fetching…' : 'Refresh AUM'}
      </Button>
    </div>
  );
};
